import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useEffect, useState } from 'react'
import { NotificationSettings, RESULTS, checkNotifications } from 'react-native-permissions'
import { AppState, AppStateStatus, PushNotificationPermissions } from 'react-native'
import { Bugsnag, Device } from '@src/config'
import { useImpersonationContext } from '@src/config/impersonation'
import { unitSystemStoreStateSelector } from '@src/selectors/settings'
import { getGlucoseUnit, getGlucoseValue } from '@src/utils/units'
import { useDispatchAsync } from '@src/utils/dispatch'
import HealthKit from '@src/services/HealthKit'
import { TerraProviderKind } from '@src/types'
import { userSelector } from '@src/selectors/app'
import terraHealthConnect from '@src/services/TerraHealthConnect/terraHealthConnect'
import { terraProvidersSelector } from '@src/selectors/integrations'
import { UnitSystem } from '../models/settings.types'
import { GenerateTerraSdkTokenQuery } from '../graphql/generateTerraSdkToken.generated'

export const useGlucosePrecision = () => {
  const unitSystem = useSelector(unitSystemStoreStateSelector)
  return unitSystem === UnitSystem.Metric ? 1 : 0
}

type NotificationPermissions = { disabled: boolean } & (
  | PushNotificationPermissions
  | NotificationSettings
)

export const useNotificationsPermissionsStatus = () => {
  const [currentPermissions, setCurrentPermissions] = useState<NotificationPermissions>()
  const [nextState, setNextState] = useState<AppStateStatus | null>(null)

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      setNextState(nextAppState)
    })

    return () => {
      subscription.remove()
    }
  }, [])

  useEffect(() => {
    if (AppState.currentState !== 'active') {
      return
    }
    checkNotifications().then(({ status, settings }) => {
      setCurrentPermissions({
        disabled: status === RESULTS.DENIED || status === RESULTS.BLOCKED,
        ...settings,
      })
    })
  }, [nextState])

  return currentPermissions
}

export const useGetGlucoseDescriptor = () => {
  const unitSystem = useSelector(unitSystemStoreStateSelector)

  return (valueInMgPerDl: number) => ({
    value: getGlucoseValue(unitSystem, valueInMgPerDl),
    unit: getGlucoseUnit(unitSystem),
  })
}

export const useNativeHealthKit = () => {
  const dispatchAsync = useDispatchAsync()
  const dispatch = useDispatch()
  const user = useSelector(userSelector)
  const isImpersonating = useImpersonationContext()

  return async () => {
    if (isImpersonating || !user?.id || !Device.hasHealthKit) {
      return false
    }

    try {
      await HealthKit.initialize()
      await dispatchAsync({
        type: 'settings/updateHealthKitSync',
        payload: { healthKitSync: true },
      })
    } catch (error: any) {
      Bugsnag.notify(error)
      return false
    }

    dispatch({ type: 'app/updateHealthData' })
    return true
  }
}

export const useTerraHealthConnect = () => {
  const dispatchAsync = useDispatchAsync()
  const user = useSelector(userSelector)

  const userId = user?.id

  const providers = useSelector(terraProvidersSelector)
  const healthConnectProvider = providers.find(
    (provider) => provider.providerKind === TerraProviderKind.HealthConnect,
  )

  const healthConnectActive = healthConnectProvider?.active

  const initConnection = useCallback(async () => {
    if (!userId) {
      return
    }

    try {
      const result = await terraHealthConnect.initializeConnection({
        getToken: async () => {
          const { token } = await dispatchAsync<
            GenerateTerraSdkTokenQuery['generateTerraSdkToken']
          >({
            type: 'settings/generateTerraSdkToken',
          })
          return token
        },
        userId,
        forceNewConnection: !healthConnectActive,
      })
      return result.success
    } catch (error: any) {
      Bugsnag.notify(error)
      return false
    }
  }, [userId, dispatchAsync, healthConnectActive])

  return initConnection
}
